import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RotateCcw, Trash2, Repeat, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const fmtWhen = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      timeZone: "America/New_York", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return iso; }
};

// Admin-only trash: soft-deleted appointments with who/when + restore.
export default function AppointmentsTrash({ onRestored }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/appointments?deleted=1");
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const restore = async (a) => {
    try {
      await api.post(`/appointments/${a.id}/restore`);
      toast.success("Cita restaurada");
      load();
      onRestored?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const purge = async (a) => {
    if (!window.confirm(`¿Eliminar DEFINITIVAMENTE la cita de ${a.passenger_name || "sin nombre"}?\n\nSe borra de la base de datos y no se puede recuperar.`)) return;
    try {
      await api.delete(`/appointments/${a.id}/purge`);
      toast.success("Cita eliminada definitivamente");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <Card className="p-8 text-muted-foreground text-center bg-card border-border">Cargando papelera…</Card>;
  if (rows.length === 0) {
    return (
      <Card className="p-10 text-center bg-card border-border" data-testid="appt-trash-empty">
        <Trash2 className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
        <div className="text-muted-foreground">La papelera está vacía.</div>
      </Card>
    );
  }
  return (
    <div className="grid gap-2" data-testid="appt-trash-list">
      {rows.map((a) => (
        <Card key={a.id} className="p-3 bg-card border-border border-dashed opacity-80" data-testid={`appt-trash-${a.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-1 min-w-[200px]">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-white line-through decoration-red-400/60">{a.passenger_name || "Sin nombre"}</span>
                <span className="text-xs font-mono text-primary">{String(a.hour).padStart(2, "0")}:{String(a.minute).padStart(2, "0")}</span>
                {a.weekdays?.length > 0
                  ? <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 gap-1"><Repeat className="w-3 h-3" /> semanal</Badge>
                  : <Badge variant="outline" className="border-sky-500/40 text-sky-300 gap-1"><Clock className="w-3 h-3" /> única</Badge>}
              </div>
              <div className="text-[11px] text-muted-foreground">{a.pickup_address || "—"} → {a.dropoff_address || "—"}</div>
              <div className="text-[11px] font-mono text-red-300/80" data-testid={`appt-trash-meta-${a.id}`}>
                Eliminada por <b>{a.deleted_by_name || "—"}</b> · {fmtWhen(a.deleted_at)}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="sm" onClick={() => restore(a)}
                className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 h-8 font-bold text-xs"
                data-testid={`appt-restore-${a.id}`}
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restaurar
              </Button>
              <Button
                variant="outline" size="sm" onClick={() => purge(a)}
                className="border-red-500/40 text-red-300 hover:bg-red-500/10 h-8 font-bold text-xs"
                data-testid={`appt-purge-${a.id}`}
                title="Borrar definitivamente de la papelera"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
